import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Add20Regular, PlugConnected20Regular } from '@fluentui/react-icons';
import { useConnections } from '../contexts/ConnectionsContext';
import CardListPage from '../components/shared/CardListPage';
import DataSourceCard from '../components/datasources/DataSourceCard';
import ConnectorPickerPanel from '../components/datasources/ConnectorPickerPanel';
import { MutationButton, MutationOnly } from '../components/security/WorkspaceAccess';

const useStyles = makeStyles({
  addCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    backgroundColor: 'transparent',
    cursor: 'pointer',
    minHeight: '140px',
    transitionProperty: 'border-color, background-color, box-shadow',
    transitionDuration: '180ms',
    ':hover': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      backgroundColor: tokens.colorNeutralBackground2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    },
  },
  addIcon: {
    fontSize: '32px',
    color: tokens.colorNeutralForeground4,
  },
  addLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  addSubtitle: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    textAlign: 'center' as const,
  },
});

const SORT_OPTIONS = [
  { value: 'updated', label: 'Last Updated' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'name_desc', label: 'Name Z-A' },
  { value: 'tables', label: 'Most Tables' },
];

const DataSourceListPage: React.FC = () => {
  const cardStyles = useStyles();
  const { connections, loading, deleteConnection } = useConnections();
  const navigate = useNavigate();

  const [panelOpen, setPanelOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortValue, setSortValue] = useState('updated');

  const handleDelete = useCallback(
    async (id: string) => { await deleteConnection(id); },
    [deleteConnection],
  );

  // Subtitle stats for TopBar
  const subtitle = useMemo(() => {
    const totalTables = connections.reduce((sum, c) => sum + (c.datasetCount ?? 0), 0);
    const syncing = connections.filter((c) => c.status === 'syncing').length;
    const parts = [`${connections.length} sources`, `${totalTables} tables`];
    if (syncing > 0) parts.push(`${syncing} syncing`);
    return parts.join(' · ');
  }, [connections]);

  // Filter + sort
  const filteredItems = useMemo(() => {
    let items = connections;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.connector?.name ?? '').toLowerCase().includes(q),
      );
    }

    items = [...items].sort((a, b) => {
      switch (sortValue) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'tables':
          return (b.datasetCount ?? 0) - (a.datasetCount ?? 0);
        case 'updated':
        default:
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
    });

    return items;
  }, [connections, searchQuery, sortValue]);


  return (
    <>
      <CardListPage
        title="Data Sources"
        subtitle={subtitle}
        layout="grid"
        searchPlaceholder="Search data sources..."
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortOptions={SORT_OPTIONS}
        sortValue={sortValue}
        onSortChange={setSortValue}
        isLoading={loading}
        isEmpty={connections.length === 0}
        filteredCount={filteredItems.length}
        persistentCard={(
          <MutationOnly>
            <div
              className={cardStyles.addCard}
              onClick={() => setPanelOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPanelOpen(true); } }}
            >
              <Add20Regular className={cardStyles.addIcon} />
              <Text className={cardStyles.addLabel}>Connect Source</Text>
              <Text className={cardStyles.addSubtitle}>Add CRM, database or file source</Text>
            </div>
          </MutationOnly>
        )}
        emptyIcon={<PlugConnected20Regular />}
        emptyTitle="No data sources connected yet"
        emptyDescription="Connect a data source so the AI can analyze, explain, and generate insights from your data."
        emptyAction={(
          <MutationOnly>
            <MutationButton appearance="primary" icon={<Add20Regular />} onClick={() => setPanelOpen(true)}>
              Add Your First Data Source
            </MutationButton>
          </MutationOnly>
        )}
      >
        {filteredItems.map((item) => (
          <DataSourceCard
            key={item.id}
            item={item}
            onClick={() => navigate(`/data-sources/${item.id}`)}
            onDelete={handleDelete}
          />
        ))}
      </CardListPage>

      <MutationOnly>
        <ConnectorPickerPanel isOpen={panelOpen} onClose={() => setPanelOpen(false)} />
      </MutationOnly>
    </>
  );
};

export default DataSourceListPage;